import { useCallback, useEffect, useRef } from 'react';
import moment from 'moment/moment';

import { formatPeriodDate, getFiltersWithoutSegment } from '../../../helpers/utils/functions';
import { showErrorMessage } from '../../../helpers/utils/ui';
import { useActions } from './useActions';
import { useTypedSelector } from './useTypedSelector';
import { Roles } from '../constants/user';

interface UseSaveReportShortcutOptions {
  /**
   * Включить/выключить обработку горячих клавиш
   */
  enabled?: boolean;
  /**
   * Callback при невозможности сохранить (новый отчёт, нет прав).
   * Используется для открытия модального окна "Сохранить как".
   */
  onSaveAs?: () => void;
}

/**
 * Хук для сохранения отчёта по Ctrl+S (Windows/Linux) или Cmd+S (macOS)
 * 
 * @param options - опции хука
 * @returns объект с функцией save для ручного вызова
 */
export const useSaveReportShortcut = (options: UseSaveReportShortcutOptions = {}) => {
  const { enabled = true, onSaveAs } = options;
  
  const { role } = useTypedSelector((state) => state.currentUser);
  const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
  const { currentReport, savedReports, isLoadingReports } = useTypedSelector(
    (state) => state.reports
  );
  const period = useTypedSelector((state) => state.period);
  const { details } = useTypedSelector((state) => state.details);
  const { filters, currentSegment, currentFunnel } = useTypedSelector((state) => state.filter);
  const { attributes } = useTypedSelector((state) => state.attributes);
  const { chartType, isChartVisible, isAxesVisible, cumulativeChart } = useTypedSelector((state) => state.chart);
  const {
    metrics,
    groupings,
    groupsChecked,
    metricsChecked,
    filtersTable,
    sortTable,
    groupsSelectionMode,
    selectFirstNRows,
    totalChecked,
    percentages,
    sparklines,
    cellFills,
    frozensColumns,
  } = useTypedSelector((state) => state.table);
  const { isCompare } = useTypedSelector((state) => state.meta);

  const { putReport } = useActions();

  // Ref для предотвращения множественных сохранений
  const isSavingRef = useRef(false);

  // Проверяем, можно ли сохранять (есть права на редактирование)
  const canSave = useCallback(() => {
    // Нет текущего отчёта
    if (Object.keys(currentReport).length === 0 || !currentReport.id) {
      return false;
    }

    // Режим сравнения
    if (isCompare) {
      return false;
    }

    // Проверяем права доступа
    const report = savedReports.find(
      (r) => r?.hash === currentReport?.hash || r?.id === currentReport?.id
    );
    if (report && report.access_id > Roles[role]) {
      return false;
    }

    return true;
  }, [currentReport, isCompare, savedReports, role]);

  // Функция сохранения отчёта
  const save = useCallback(() => {
    if (isSavingRef.current) {
      return;
    }

    if (!canSave()) {
      // Если режим сравнения — "Сохранить как" тоже неуместен
      if (isCompare) {
        showErrorMessage('Сохранение недоступно в режиме сравнения');
        return;
      }

      // Новый отчёт (нет id) или нет прав — открываем "Сохранить как"
      if (onSaveAs) {
        onSaveAs();
      } else {
        if (Object.keys(currentReport).length === 0 || !currentReport.id) {
          showErrorMessage('Сначала откройте или создайте отчёт');
        } else {
          showErrorMessage('Недостаточно прав для сохранения этого отчёта');
        }
      }
      return;
    }

    if (!selectedSchool?.id) {
      showErrorMessage('Школа не выбрана');
      return;
    }

    isSavingRef.current = true;

    const current_un_period_selection = period.select;
    
    // Формируем данные для сохранения (аналогично un-title/UniversalReportTitle.save)
    let values: any = {
      ...currentReport.values,
      period: {
        start: formatPeriodDate(period.start, period.withTime),
        end: formatPeriodDate(period.end, period.withTime),
        interval: period.interval || 'day',
      },
      details,
      chartType,
      groupings,
      metrics,
      groupsChecked,
      metricsChecked,
      filtersTable,
      sortTable,
      attributes,
      current_un_period_selection: current_un_period_selection
        ? current_un_period_selection
        : '',
      segment: null,
      filtersReq: attributes,
      // Сохраняем настройки автовыбора группировок
      groupsSelectionMode: groupsSelectionMode || 'manual',
      selectFirstNRows: selectFirstNRows,
      // Сохраняем состояние "Итого и среднее"
      totalChecked: totalChecked,
      // Сохраняем воронку продаж
      currentFunnel: currentFunnel,
      // Сохраняем настройки графика
      isChartVisible: isChartVisible,
      isAxesVisible: isAxesVisible,
      cumulativeChart: cumulativeChart,
      // Сохраняем процент в колонках и закреплённые столбцы
      percentages: percentages,
      // Колонки со спарклайном
      sparklines: sparklines,
      // Заливка ячеек обычных метрик по условиям «от – до»
      cellFills: cellFills,
      frozensColumns: frozensColumns,
    };

    // Обработка сегмента
    if (Object.keys(currentSegment || {}).length > 0) {
      values.segment = currentSegment;
      values.filters = getFiltersWithoutSegment(
        filters,
        currentSegment?.values?.filters
      );
    } else {
      values.segment = {};
      values.filters = filters;
      values.attributes = attributes;
    }

    // Обработка периода "N дней назад"
    if (period.interval === 'ndaysago' || period.select === 'ndaysago') {
      values.period.days = period.days;
    }

    // Вызываем сохранение
    putReport(selectedSchool.id, { id: currentReport.id, values });

    // Сбрасываем флаг через небольшую задержку
    setTimeout(() => {
      isSavingRef.current = false;
    }, 1000);
  }, [
    canSave,
    onSaveAs,
    currentReport,
    isCompare,
    selectedSchool,
    period,
    details,
    chartType,
    groupings,
    metrics,
    groupsChecked,
    metricsChecked,
    filtersTable,
    sortTable,
    attributes,
    groupsSelectionMode,
    selectFirstNRows,
    totalChecked,
    currentFunnel,
    isChartVisible,
    isAxesVisible,
    cumulativeChart,
    percentages,
    sparklines,
    cellFills,
    frozensColumns,
    filters,
    currentSegment,
    putReport,
  ]);

  // Обработчик клавиатуры
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+S (Windows/Linux) или Cmd+S (macOS)
      const isSaveShortcut = (e.ctrlKey || e.metaKey) && e.key === 's';
      
      if (isSaveShortcut) {
        // Предотвращаем стандартное поведение браузера (сохранение страницы)
        e.preventDefault();
        e.stopPropagation();
        
        save();
      }
    };

    // Используем capture: true чтобы перехватить событие раньше других обработчиков
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [enabled, save]);

  return {
    save,
    canSave: canSave(),
    isLoading: isLoadingReports,
  };
};

export default useSaveReportShortcut;
